import { foodDatabase, type FoodItem, type FoodNutrition } from "@/mocks/fooddb";

export interface ParsedFood {
  foodId: string;
  name: string;
  category: string;
  quantity: number;
  unit: string;
  weight: number;
  confidence: number;
  per100g: FoodNutrition;
  nutrition: FoodNutrition;
}

const numMap: Record<string, number> = {
  半: 0.5,
  一: 1,
  两: 2,
  二: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  七: 7,
  八: 8,
  九: 9,
  十: 10,
};

function parseNum(s: string): number {
  const n = Number(s);
  if (!Number.isNaN(n)) return n;
  if (numMap[s] !== undefined) return numMap[s];
  return 1;
}

export function scaleNutrition(per: FoodNutrition, weight: number): FoodNutrition {
  const k = weight / 100;
  const r1 = (v: number) => Math.round(v * k * 10) / 10;
  return {
    calories: Math.round(per.calories * k),
    protein: r1(per.protein),
    carbs: r1(per.carbs),
    fat: r1(per.fat),
    fiber: r1(per.fiber),
    vitC: r1(per.vitC),
  };
}

export function nutritionToPer100g(n: FoodNutrition, weight: number): FoodNutrition {
  const k = 100 / (weight || 1);
  const r1 = (v: number) => Math.round(v * k * 100) / 100;
  return {
    calories: Math.round(n.calories * k),
    protein: r1(n.protein),
    carbs: r1(n.carbs),
    fat: r1(n.fat),
    fiber: r1(n.fiber),
    vitC: r1(n.vitC),
  };
}

export function findFood(text: string): FoodItem | null {
  let best: FoodItem | null = null;
  let bestLen = 0;
  for (const food of foodDatabase) {
    const names = [food.name, ...food.aliases];
    for (const n of names) {
      if (text.includes(n) && n.length > bestLen) {
        best = food;
        bestLen = n.length;
      }
    }
  }
  return best;
}

interface Amount {
  quantity: number;
  unit: string;
  weight: number | null;
}

function parseAmount(text: string): Amount {
  let quantity = 1;
  let unit = "份";
  let weight: number | null = null;

  const gram = text.match(/([0-9.]+|[一二两三四五六七八九十半]+)\s*(公斤|千克|kg|KG|克|g|G)/);
  if (gram) {
    const v = parseNum(gram[1]);
    const isKg = /公斤|千克|kg|KG/.test(gram[2]);
    weight = isKg ? Math.round(v * 1000) : Math.round(v);
  }

  const q = text.match(/([0-9.]+|[一二两三四五六七八九十半]+)\s*(个|碗|份|杯|只|根|块|片|盒|串|颗|把|盘)/);
  if (q) {
    quantity = parseNum(q[1]);
    unit = q[2];
  }

  return { quantity, unit, weight };
}

export function parseFood(text: string): ParsedFood | null {
  const food = findFood(text);
  if (!food) return null;
  const { quantity, unit, weight } = parseAmount(text);
  const finalWeight = weight ?? Math.max(1, Math.round(food.unitWeight * quantity));
  const nutrition = scaleNutrition(food.per100g, finalWeight);
  return {
    foodId: food.id,
    name: food.name,
    category: food.category,
    quantity,
    unit: weight !== null ? "克" : unit,
    weight: finalWeight,
    confidence: 95,
    per100g: food.per100g,
    nutrition,
  };
}

// 把一段文字拆成多个食物片段，支持 、 ， , ; ； 换行 以及 和/加/还有/加上 等连接词
function splitFoods(text: string): string[] {
  return text
    .replace(/(还有|加上|以及|再来|和|加)/g, "、")
    .split(/[、，,;；\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// 去掉片段中的数量词前缀，用于未命中食物库时的兜底命名
function stripAmount(text: string): string {
  return text
    .replace(/^[0-9.]+\s*(个|碗|份|杯|只|根|块|片|盒|串|颗|把|盘)?/, "")
    .replace(/^[一二两三四五六七八九十半]+\s*(个|碗|份|杯|只|根|块|片|盒|串|颗|把|盘)?/, "")
    .trim();
}

const fallbackPer100g: FoodNutrition = { calories: 150, protein: 5, carbs: 15, fat: 8, fiber: 1, vitC: 5 };

// 一次解析整段输入中的全部食物，保证一个都不丢
export function parseFoods(text: string): ParsedFood[] {
  const segments = splitFoods(text);
  const results: ParsedFood[] = [];

  for (const seg of segments) {
    const parsed = parseFood(seg);
    if (parsed) {
      results.push(parsed);
      continue;
    }
    // 未命中食物库：仍然记录，避免丢失
    const { quantity, unit, weight } = parseAmount(seg);
    const name = stripAmount(seg) || seg;
    const finalWeight = weight ?? 100;
    results.push({
      foodId: `custom-${seg}`,
      name,
      category: "其他",
      quantity,
      unit: weight !== null ? "克" : unit,
      weight: finalWeight,
      confidence: 60,
      per100g: fallbackPer100g,
      nutrition: scaleNutrition(fallbackPer100g, finalWeight),
    });
  }

  return results;
}